import { useMemo } from 'react';

import { useAtomValue } from 'jotai';
import { useCurrentFrame } from 'remotion';
import * as THREE from 'three';

import { CameraSetup } from '@data';
import { selectedActiveAnalysisAtom, cameraSetupMapAtom } from '@store';

export const BALL_POSITION: [number, number, number] = [0.081, 0.355, -0.045];

const QUICK_ANALYSIS_FRAME_THRESHOLD = 10;

export type KeypointPosition = [number, number, number];
export type Frame = Record<string, KeypointPosition>;
type Point2D = [number, number];
type Point3D = [number, number, number];

function buildInverseCameraMatrix(cameraSetup: CameraSetup): THREE.Matrix4 {
    const matrix = new THREE.Matrix4();
    const flatValues = cameraSetup.cameraInWorld.flat();
    matrix.set(
        flatValues[0],
        flatValues[1],
        flatValues[2],
        flatValues[3],
        flatValues[4],
        flatValues[5],
        flatValues[6],
        flatValues[7],
        flatValues[8],
        flatValues[9],
        flatValues[10],
        flatValues[11],
        flatValues[12],
        flatValues[13],
        flatValues[14],
        flatValues[15],
    );
    matrix.invert();
    return matrix;
}

function transformWorldToCamera(cameraMatrix: THREE.Matrix4, points: Point3D[]): Point3D[] {
    return points.map(([x, y, z]) => {
        const homogeneous = new THREE.Vector4(x, y, z, 1);
        homogeneous.applyMatrix4(cameraMatrix);
        return [homogeneous.x, homogeneous.y, homogeneous.z];
    });
}

function projectToImagePlane(points: Point3D[], cameraSetup: CameraSetup): Point2D[] {
    const { fx, fy, cx, cy } = cameraSetup.intrinsics;
    return points.map(([x, y, z]) => [(x * fx) / z + cx, (y * fy) / z + cy]);
}

function correctLensDistortion(points: Point2D[], cameraSetup: CameraSetup): Point2D[] {
    const { fx, fy, cx, cy, distortionCoeffs } = cameraSetup.intrinsics;
    const [k1, k2, p1, p2, k3] = distortionCoeffs;

    return points.map(([x, y]) => {
        const normalizedX = (x - cx) / fx;
        const normalizedY = (y - cy) / fy;

        const r2 = normalizedX ** 2 + normalizedY ** 2;
        const radialFactor = 1 + k1 * r2 + k2 * r2 ** 2 + k3 * r2 ** 3;

        const correctedX =
            normalizedX * radialFactor + 2 * p1 * normalizedX * normalizedY + p2 * (r2 + 2 * normalizedX ** 2);

        const correctedY =
            normalizedY * radialFactor + p1 * (r2 + 2 * normalizedY ** 2) + 2 * p2 * normalizedX * normalizedY;

        return [correctedX * fx + cx, correctedY * fy + cy];
    });
}

export function worldPointsToImageCoordinates(points: Point3D[], cameraSetup: CameraSetup): Point2D[] {
    const inverseCameraMatrix = buildInverseCameraMatrix(cameraSetup);

    if (!inverseCameraMatrix) {
        return [];
    }

    const cameraSpacePoints = transformWorldToCamera(inverseCameraMatrix, points);
    const projectedPoints = projectToImagePlane(cameraSpacePoints, cameraSetup);
    return correctLensDistortion(projectedPoints, cameraSetup);
}

export function reshapeSkeletonToFrames(skeleton: Record<string, [number[], number[], number[]]>): Frame[] {
    const keypointIds = Object.keys(skeleton);
    const firstKeypoint = skeleton[keypointIds[0]];

    if (!firstKeypoint) {
        return [];
    }

    const frameCount = firstKeypoint[0].length;

    return Array.from({ length: frameCount }, (_, frameIndex) =>
        Object.fromEntries(
            keypointIds.map((keypointId) => {
                const [xValues, yValues, zValues] = skeleton[keypointId];
                const keypointPosition: KeypointPosition = [
                    xValues[frameIndex],
                    yValues[frameIndex],
                    zValues[frameIndex],
                ];
                return [keypointId, keypointPosition];
            }),
        ),
    );
}

function buildKeypointMap(skeletonFrame: Frame, cameraSetup: CameraSetup): Record<string, Point2D> {
    const keypointIds = Object.keys(skeletonFrame);
    const worldPoints = Object.values(skeletonFrame);
    const imagePoints = worldPointsToImageCoordinates(worldPoints, cameraSetup);

    return Object.fromEntries(keypointIds.map((id, index) => [id, imagePoints[index]]));
}

export function useSkeletonData(cameraView: number) {
    const currentFrame = useCurrentFrame();
    const activeAnalysis = useAtomValue(selectedActiveAnalysisAtom);
    const cameraSetupMap = useAtomValue(cameraSetupMapAtom);

    const cameraSetup = cameraSetupMap?.[cameraView];

    const skeleton = activeAnalysis?.data.analysis?.skeleton;
    const bones = activeAnalysis?.data.analysis?.bones;

    const frames = useMemo(() => (skeleton ? reshapeSkeletonToFrames(skeleton) : null), [skeleton]);

    return useMemo(() => {
        if (!cameraSetup || !activeAnalysis || !frames || !Array.isArray(bones)) {
            return null;
        }

        const segmentationValues = Object.values(activeAnalysis.data.analysis?.segmentation ?? {});
        const quickAnalysisFrameIndex = segmentationValues.indexOf(currentFrame);

        const isQuickAnalysis = frames.length <= QUICK_ANALYSIS_FRAME_THRESHOLD;
        const frameIndex = isQuickAnalysis ? quickAnalysisFrameIndex : currentFrame;

        const skeletonFrame = frames[frameIndex];
        if (!skeletonFrame) {
            return null;
        }

        const keypointMap = buildKeypointMap(skeletonFrame, cameraSetup);

        return { bones, keypointMap };
    }, [cameraSetup, activeAnalysis, frames, bones, currentFrame]);
}
